/**
 * <s-multi-variant-add> — Lets a shopper set a quantity for several variants
 * (for example, two colours) and add them to the cart in one request.
 *
 * Commands:
 *   --add — Adds every row with a quantity above zero as its own cart line.
 *
 * Required child markup:
 *   [data-multi-variant][data-variant-id][data-price] — One row per variant; price in cents.
 *   input[type="number"] inside each available row    — The row quantity.
 *   [data-multi-total]                                — Running total, formatted with data-money-format.
 *   [data-multi-label]                                — Add button label, updated with the item count.
 *   [data-multi-error]                                — Error message region.
 *
 * Element dataset:
 *   data-money-format  — shop.money_format.
 *   data-label-none / data-label-one / data-label-many — Button labels; many contains __count__.
 *   data-empty-message / data-error-message — i18n error copy.
 *   data-cart-add-url / data-cart-url — Liquid routes for the no-Standard-Actions fallback.
 */
class SMultiVariantAdd extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    const refresh = () => this.#refresh();
    this.addEventListener('input', refresh, opts);
    this.addEventListener('change', refresh, opts);
    this.addEventListener('s-quantity:change', refresh, opts);
    this.addEventListener('command', (event) => {
      if (event.command === '--add') this.#add(event.source);
      else if (event.command === '--step-up') this.#step(event.source, 1);
      else if (event.command === '--step-down') this.#step(event.source, -1);
    }, opts);

    this.#refresh();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #rows() {
    return Array.from(this.querySelectorAll('[data-multi-variant]')).map((row) => {
      const input = row.querySelector('input[type="number"]');
      const quantity = input && !input.disabled ? Number.parseInt(input.value, 10) : 0;
      return {
        row,
        input,
        id: row.dataset.variantId,
        price: Number(row.dataset.price) || 0,
        quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 0,
      };
    });
  }

  #step(source, direction) {
    const id = source?.dataset?.variantId;
    const item = this.#rows().find((entry) => entry.id === id);
    const input = item?.input;
    if (!input || input.disabled) return;

    const step = Number.parseInt(input.step, 10) || 1;
    const max = Number.parseInt(input.max, 10);
    let next = item.quantity + (direction * step);
    next = Math.max(0, next);
    if (Number.isFinite(max)) next = Math.min(max, next);

    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #refresh() {
    for (const { row, input, quantity } of this.#rows()) {
      if (!input) continue;
      const max = Number.parseInt(input.max, 10);
      const down = row.querySelector('[command="--step-down"]');
      const up = row.querySelector('[command="--step-up"]');
      down?.setAttribute('aria-disabled', String(quantity <= 0));
      up?.setAttribute('aria-disabled', String(Number.isFinite(max) && quantity >= max));
    }

    const chosen = this.#rows().filter((item) => item.quantity > 0);
    const count = chosen.reduce((sum, item) => sum + item.quantity, 0);
    const total = chosen.reduce((sum, item) => sum + item.price * item.quantity, 0);

    const totalEl = this.querySelector('[data-multi-total]');
    if (totalEl) totalEl.textContent = this.#money(total);

    const label = this.querySelector('[data-multi-label]');
    if (label) {
      if (count === 0) label.textContent = this.dataset.labelNone || '';
      else if (count === 1) label.textContent = this.dataset.labelOne || '';
      else label.textContent = (this.dataset.labelMany || '').replace('__count__', String(count));
    }

    if (count > 0) this.#showError('');
  }

  async #add(button) {
    if (this.getAttribute('aria-busy') === 'true') return;

    const lines = this.#rows()
      .filter((item) => item.quantity > 0)
      .map((item) => ({ merchandiseId: item.id, quantity: item.quantity }));

    if (!lines.length) {
      this.#showError(this.dataset.emptyMessage || '');
      return;
    }

    this.setAttribute('aria-busy', 'true');
    button?.setAttribute('aria-disabled', 'true');
    button?.classList.add('opacity-50', 'cursor-wait');
    this.#showError('');

    try {
      if (window.Shopify?.actions?.updateCart) {
        const result = await window.Shopify.actions.updateCart(
          { lines },
          { event: { context: 'product', detail: { source: 's-multi-variant-add' } } }
        );
        const message = result?.userErrors?.[0]?.message;
        if (message) throw new Error(message);
        this.#reset();
        await window.Shopify.actions.openCart?.();
      } else {
        // cart/add.js returns JSON; the route base comes from Liquid via data-cart-add-url.
        const items = lines.map((line) => ({ id: Number(line.merchandiseId), quantity: line.quantity }));
        const response = await fetch(`${this.dataset.cartAddUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!response.ok) {
          const data = await response.json().catch(() => null);
          throw new Error(data?.description || '');
        }
        window.location.href = this.dataset.cartUrl;
      }
    } catch (error) {
      this.#showError(error?.message || this.dataset.errorMessage || '');
    } finally {
      this.removeAttribute('aria-busy');
      button?.removeAttribute('aria-disabled');
      button?.classList.remove('opacity-50', 'cursor-wait');
    }
  }

  #reset() {
    for (const { input } of this.#rows()) {
      if (!input || input.disabled) continue;
      input.value = '0';
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.#refresh();
  }

  #showError(message) {
    const error = this.querySelector('[data-multi-error]');
    if (!error) return;
    error.textContent = message || '';
    error.hidden = !message;
  }

  #money(cents) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const value = cents / 100;
    const group = (num, decimals, thousands, decimal) => {
      const [whole, frac] = num.toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? `${grouped}${decimal}${frac}` : grouped;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return group(value, 0, ',', '.');
        case 'amount_with_comma_separator': return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(value, 0, '.', ',');
        case 'amount_with_space_separator': return group(value, 2, ' ', ',');
        case 'amount_with_apostrophe_separator': return group(value, 2, "'", '.');
        default: return group(value, 2, ',', '.');
      }
    });
  }
}

if (!customElements.get('s-multi-variant-add')) {
  customElements.define('s-multi-variant-add', SMultiVariantAdd);
}
